import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useProduct, useProducts, money, discountPct, cleanTitle } from "@/lib/api";
import { useCart, toLine } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { Price, ProductGrid } from "@/components/shop/ProductCard";
import { TrustBar } from "@/components/shop/Sections";
import { FrequentlyBought } from "@/components/shop/FrequentlyBought";
import { ProductReviews, RatingSummary } from "@/components/shop/ProductReviews";
import { SplitText, Reveal } from "@/components/fx/Motion";
import { ProductStory, StoryIntro } from "@/components/shop/ProductStory";
import { BeforeAfter } from "@/components/shop/BeforeAfter";
import { LIFESTYLE, LIFESTYLE_SET, PHOTO } from "@/lib/content";
import { useXmasImage, useGirlPhoto } from "@/lib/holiday";
import { useSeo } from "@/lib/seo";
import { MessageCircle } from "lucide-react";
import { WHATSAPP } from "@/components/shop/Social";

const Gallery = ({ images, title }) => {
  const [i, setI] = useState(0);
  useEffect(() => { setI(0); }, [images]);
  return (
    <div className="lg:sticky lg:top-28 self-start">
      <motion.div className="aspect-square bg-white overflow-hidden relative" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}>
        <AnimatePresence mode="wait">
          <motion.img key={images[i]} data-testid="product-main-image" src={images[i]} alt={title} className={`absolute inset-0 h-full w-full hover:scale-110 transition-transform duration-700 ${LIFESTYLE_SET.has(images[i]) ? "object-cover" : "object-contain p-6"}`}
            initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.5 }} />
        </AnimatePresence>
      </motion.div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
          {images.map((src, idx) => (
            <button key={src} data-testid={`product-thumb-${idx}`} onClick={() => setI(idx)} className={`h-20 w-20 shrink-0 bg-white border-2 transition-colors ${idx === i ? "border-[#c9a55c]" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={src} alt="" className={`h-full w-full ${LIFESTYLE_SET.has(src) ? "object-cover" : "object-contain p-1"}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Qty = ({ qty, setQty }) => (
  <div className="flex items-center border border-white/15 h-14">
    <button data-testid="product-qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-full flex items-center justify-center hover:text-gold"><Minus size={15} /></button>
    <motion.span key={qty} initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="product-qty-value" className="w-10 text-center">{qty}</motion.span>
    <button data-testid="product-qty-increase" onClick={() => setQty(Math.min(99, qty + 1))} className="w-12 h-full flex items-center justify-center hover:text-gold"><Plus size={15} /></button>
  </div>
);

const StickyBar = ({ p, v, qty, line }) => {
  const { t } = useI18n();
  useEffect(() => { document.body.classList.add("has-sticky-bar"); return () => document.body.classList.remove("has-sticky-bar"); }, []);
  const { add, checkout } = useCart();
  return (
    <motion.div initial={{ y: 100 }} animate={{ y: 0 }} transition={{ delay: 0.8, type: "spring", stiffness: 120 }} data-testid="mobile-sticky-buy-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0b0a08]/95 backdrop-blur-xl border-t border-white/10 p-3 flex items-center gap-3">
      <div className="flex-1 min-w-0"><p className="text-xs truncate text-white/70">{cleanTitle(p.title)}</p><p className="text-sm font-semibold">{money(v.price * qty)}</p></div>
      <button data-testid="mobile-sticky-add-button" disabled={!v.available} onClick={() => add(line)} className="btn-ghost h-12 px-4">{t("card_add")}</button>
      <button data-testid="mobile-sticky-buy-now-button" disabled={!v.available} onClick={() => checkout([line])} className="btn-gold h-12 px-6">{t("buy_now")}</button>
    </motion.div>
  );
};

const Variants = ({ p, v, setVid, t }) => (
  <div className="mt-8">
    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-3">{p.options[0]?.name || t("option")}</p>
    <div className="flex flex-wrap gap-2">
      {p.variants.map((x) => (
        <button key={x.id} data-testid={`product-variant-${x.id}`} disabled={!x.available} onClick={() => setVid(x.id)}
          className={`px-5 h-11 border text-sm transition-colors disabled:opacity-30 ${x.id === v.id ? "border-[#c9a55c] text-gold" : "border-white/15 hover:border-white/40"}`}>{x.title}</button>
      ))}
    </div>
  </div>
);

export default function Product() {
  const { handle } = useParams();
  const { t } = useI18n();
  const { data: p, isLoading, isError } = useProduct(handle);
  const { data: related = [] } = useProducts({ collection: p?.brand === "Primus" ? "primus" : "bellecare" });
  const { add, checkout, redirecting } = useCart();
  const [vid, setVid] = useState(null);
  const [qty, setQty] = useState(1);
  useEffect(() => { setVid(null); setQty(1); }, [handle]);
  const xmas = useXmasImage(handle);
  const girl = useGirlPhoto();
  useSeo(p ? cleanTitle(p.title_en || p.title) : null, p?.excerpt);

  if (isLoading) return <div className="max-w-7xl mx-auto px-5 py-20"><div className="aspect-square max-w-xl bg-white/5 animate-pulse" /></div>;
  if (isError || !p) return <div className="max-w-7xl mx-auto px-5 py-32 text-center"><p className="font-display text-3xl mb-6">{t("not_found")}</p><Link to="/shop" className="btn-gold px-8 py-4 inline-block">{t("back_to_shop")}</Link></div>;

  const v = p.variants.find((x) => x.id === vid) || p.variants[0];
  const line = toLine(p, v, qty);
  const pct = discountPct({ price: v.price, compare_at_price: v.compare_at_price });

  return (
    <div data-testid="product-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-16 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <Gallery images={[xmas, p.images[0], ...(LIFESTYLE[p.handle] || []).map((s) => (s === PHOTO.girl ? girl : s)), ...p.images.slice(1)].filter(Boolean)} title={p.title} />
        <div>
          <p className="text-xs text-white/40 mb-6"><Link to="/shop" className="hover:text-white">{t("shop_eyebrow")}</Link> / {p.brand}</p>
          <p className="eyebrow mb-3">{p.brand}</p>
          <h1 data-testid="product-title" className="font-display text-4xl sm:text-5xl leading-tight"><SplitText key={p.title} text={cleanTitle(p.title)} delay={0.3} /></h1>
          <Reveal delay={0.5}>
            <div className="mt-4"><RatingSummary handle={p.handle} showEmpty /></div>
            <div data-testid="product-price" className="mt-6 flex items-center gap-4">
              <Price price={v.price} compare={v.compare_at_price} big />
              {pct > 0 && <motion.span animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2, repeat: Infinity }} className="bg-gold text-[#0b0a08] text-[11px] font-bold px-2.5 py-1 uppercase">{t("save_pct", { pct })}</motion.span>}
            </div>
            {p.story ? <StoryIntro story={p.story} /> : p.excerpt && <p className="text-white/60 mt-6 leading-relaxed">{p.excerpt}…</p>}
          </Reveal>
          {p.variants.length > 1 && <Variants p={p} v={v} setVid={setVid} t={t} />}
          <Reveal delay={0.65}>
            <div className="flex gap-3 mt-8">
              <Qty qty={qty} setQty={setQty} />
              <button data-testid="product-add-to-cart-button" disabled={!v.available} onClick={() => add(line)} className="btn-ghost flex-1 h-14">{v.available ? t("add_to_bag") : t("sold_out")}</button>
            </div>
            <button data-testid="product-buy-now-button" disabled={!v.available || redirecting} onClick={() => checkout([line])} className="btn-gold btn-shine w-full h-16 mt-3 flex items-center justify-center gap-2 text-sm">
              <Lock size={15} /> {redirecting ? t("opening_checkout") : t("buy_now_total", { amount: money(v.price * qty) })}
            </button>
            <p className="text-center text-[11px] text-white/40 mt-3">{t("pdp_secure_note")}</p>
            <a data-testid="pdp-whatsapp-button" target="_blank" rel="noopener noreferrer"
              href={`${WHATSAPP.href}?text=${encodeURIComponent(t("wa_product_msg", { product: cleanTitle(p.title), url: window.location.origin + window.location.pathname }))}`}
              className="mt-4 h-12 w-full inline-flex items-center justify-center gap-2 border border-[#25D366]/50 text-sm hover:bg-[#25D366]/10 transition-colors">
              <MessageCircle size={17} className="text-[#25D366]" />{t("wa_ask")}
            </a>
          </Reveal>
          <div className="mt-10 p-6 border border-white/10"><TrustBar compact /></div>
        </div>
      </div>
      <ProductStory story={p.story} descriptionHtml={p.description_html} />
      <FrequentlyBought product={p} />
      <BeforeAfter handle={p.handle} />
      <ProductReviews product={p} />
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <h2 className="font-display text-3xl lg:text-4xl mb-10"><SplitText text={t("related_title")} /></h2>
        <ProductGrid products={related.filter((r) => r.handle !== p.handle).slice(0, 4)} testid="related-products-grid" />
      </section>
      <StickyBar p={p} v={v} qty={qty} line={line} />
    </div>
  );
}
